/**
 * Shared helpers + presentation constants for the Professional Dashboard
 * (the unified Assignments + Access Requests view).
 */

// Mirrors the backend rate limit in companyAccessController.remindAccessGrant.
export const REMINDER_COOLDOWN_MS = 24 * 60 * 60 * 1000;

/* ── Date helpers ──────────────────────────────────────────────────────── */
export function formatDate(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function formatDateTime(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function daysSince(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 0;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
}

export function timeAgo(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

/* ── Assignment presentation ───────────────────────────────────────────── */
export const PROFESSIONAL_STATUS_BADGE = {
  pending: { bg: '#f3ebd7', color: '#8a6a1f', label: 'Pending', icon: '⏳' },
  accepted: { bg: '#e8efe5', color: '#4a6b3f', label: 'Accepted', icon: '✅' },
  rejected: { bg: '#f2e9e6', color: '#9a564a', label: 'Rejected', icon: '❌' },
};

export const ASSIGNMENT_SCOPE_BADGE = {
  all_companies: { bg: '#edeef3', color: '#3a4a66', label: 'All companies', icon: '🌐' },
  specific_companies: { bg: '#efe9e0', color: '#6f6049', label: 'Specific companies', icon: '🏢' },
};

export const ACCESS_LEVEL_BADGE = {
  read: { bg: '#edeef3', color: '#3a4a66', label: 'Read', icon: '👁️' },
  read_write: { bg: '#edeef3', color: '#3a4a66', label: 'Read / Write', icon: '✏️' },
  admin: { bg: '#e8efe5', color: '#4a6b3f', label: 'Admin', icon: '👑' },
};

export const ACCESS_LEVEL_LABEL = {
  read: 'Read only',
  read_write: 'Read / Write',
  admin: 'Admin',
};

/* ── Access-request presentation ───────────────────────────────────────── */
// Resolves a grant row into how its status should be presented. `revoked`
// splits into "Declined" (never approved) vs "Revoked" (approved then pulled).
export function displayStatus(req) {
  if (req.status === 'pending') {
    return { key: 'pending', label: 'Pending', bg: '#f3ebd7', color: '#8a6a1f', dot: '#8a6a1f' };
  }
  if (req.status === 'active') {
    return { key: 'active', label: 'Granted', bg: '#e8efe5', color: '#4a6b3f', dot: '#4a6b3f' };
  }
  return req.grantedAt
    ? { key: 'revoked', label: 'Revoked', bg: '#f2e9e6', color: '#9a564a', dot: '#9a564a' }
    : { key: 'declined', label: 'Declined', bg: '#f1ede4', color: '#7a7060', dot: '#a39a88' };
}

// Aging colour escalates the longer a request has gone unanswered.
export function agingTone(days) {
  if (days >= 7) return '#9a564a';
  if (days >= 3) return '#8a6a1f';
  return '#7a7060';
}
